import { useMemo } from "react";
import useSWR from "swr";
import { parseUnits } from "viem";

import { getServerUrl } from "config/backend";
import { USD_DECIMALS } from "config/factors";

export type PeriodAccountStats = {
  closedCount: number;
  cumsumCollateral: bigint;
  cumsumSize: bigint;
  losses: number;
  maxCapital: bigint;
  netCapital: bigint;
  realizedFees: bigint;
  realizedPnl: bigint;
  realizedPriceImpact: bigint;
  startUnrealizedFees: bigint;
  startUnrealizedPnl: bigint;
  startUnrealizedPriceImpact: bigint;
  sumMaxSize: bigint;
  volume: bigint;
  wins: number;
  id: string;
};

function parseUsd(v: string | undefined | null): bigint {
  if (!v || v === "0") return 0n;
  try {
    return parseUnits(v as `${number}`, USD_DECIMALS);
  } catch (_parseUnitsError) {
    try {
      return BigInt(v);
    } catch (_bigIntError) {
      return 0n;
    }
  }
}

/** Map a timeframe window (from/to Unix timestamps) to a bucketLabel from the backend */
function getBucketLabel(from: number | undefined, to: number | undefined): string {
  if (!from) return "all";
  const nowSecs = Math.floor(Date.now() / 1000);
  const windowSecs = (to ?? nowSecs) - from;
  const days = windowSecs / 86400;
  if (days <= 1.5) return "today";
  if (days <= 7.5) return "week";
  if (days <= 30.5) return "month";
  return "all";
}

/**
 * MIGRATED: Replaced Subgraph query with /accounts/:account/pnl_summary REST endpoint.
 * Selects the matching bucket (today/week/month/all) based on from/to timestamps.
 */
export function usePeriodAccountStats(
  chainId: number,
  params: { account?: string; from?: number; to?: number; enabled?: boolean }
) {
  const { account, from, to, enabled = true } = params;

  const { data, error, isLoading } = useSWR<PeriodAccountStats | undefined>(
    enabled && account ? ["usePeriodAccountStats", from, to, chainId, account] : null,
    {
      fetcher: async () => {
        try {
          const url = getServerUrl(chainId, `/accounts/${account}/pnl_summary`);
          const response = await fetch(url);
          const json = await response.json();

          // Backend returns { points: [{ bucketLabel, pnlUsd, volume, wins, losses, ... }] }
          const bucketLabel = getBucketLabel(from, to);
          const row =
            json?.points?.find((p: any) => p.bucketLabel === bucketLabel) ??
            json?.points?.find((p: any) => p.bucketLabel === "all") ??
            json?.points?.[0];

          if (!row) return undefined;

          return {
            closedCount: (row.wins ?? 0) + (row.losses ?? 0),
            cumsumCollateral: 0n,
            cumsumSize: 0n,
            losses: row.losses ?? 0,
            maxCapital: 0n,
            netCapital: parseUsd(row.pnlUsd),
            realizedFees: 0n,
            realizedPnl: parseUsd(row.pnlUsd),
            realizedPriceImpact: 0n,
            startUnrealizedFees: 0n,
            startUnrealizedPnl: 0n,
            startUnrealizedPriceImpact: 0n,
            sumMaxSize: 0n,
            volume: parseUsd(row.volume),
            wins: row.wins ?? 0,
            id: account!,
          };
        } catch (e) {
          console.error("Failed to fetch period account stats", e);
          return undefined;
        }
      },
    }
  );

  return useMemo(() => ({ data, error, loading: isLoading }), [data, error, isLoading]);
}
